import type { ApolloError } from '@apollo/client';
import { LelandPlusBanner } from '@leland-dev/leland-ui-library';
import React, { Fragment, useMemo } from 'react';

import type { Scalars } from '../../__generated-gql-types__/globalTypes';
import LelandPlusLogo from '../../assets/svgs/leland-plus-logo.svg';
import mxpnl from '../../utils/analytics/mixpanel';
import { getCoachUrl } from '../../utils/coach';
import {
  getLelandPlusLandingPageUrl,
  isLiveLelandPlusCategory,
} from '../../utils/leland-plus';
import { getLelandPlusImages } from '../common/LelandPlusImages';

import CoachCard from './CoachCard';
import CoachCardSkeleton from './CoachCardSkeleton';
import { NoCoachResults } from './NoCoachResults';
import {
  type CoachListCategoryFragment,
  type CoachListCoachFragment,
  type CoachListHydratedCoachFieldsFragment,
  type CoachListLelandPlusContributorCoachBatchResponseFragment,
  type CoachListSkuFragment,
  useCoachListHydratedCoachesBySlugsQuery,
  useCoachListUserQuery,
} from './__generated-gql-types__/CoachList.generated';

interface CoachListProps {
  coaches: CoachListCoachFragment[];
  category: Possible<CoachListCategoryFragment>;
  subCategorySlug?: Possible<Scalars['SubCategoryV2Urn']['output']>;
  selectedSchoolIds: Array<Scalars['SchoolUrn']['output']>;
  selectedCompanyIds: Array<Scalars['CompanyUrn']['output']>;
  loading: boolean;
  error: ApolloError | undefined;
  resetVariables: () => void;
  className?: string;
  onClick?: (coach: CoachListCoachFragment, index: number) => void;
  hidePrice: boolean;
  skusData: CoachListSkuFragment[];
}

type CoachListLelandPlusBannerProps =
  | { showLelandPlusBanner?: never; lelandPlusContributors?: never }
  | {
      showLelandPlusBanner: boolean;
      lelandPlusContributors: Possible<CoachListLelandPlusContributorCoachBatchResponseFragment>;
    };

const CoachList: React.FC<CoachListProps & CoachListLelandPlusBannerProps> = ({
  coaches: staticCoaches,
  category,
  subCategorySlug,
  selectedSchoolIds,
  selectedCompanyIds,
  loading,
  error,
  resetVariables,
  className = '',
  onClick,
  showLelandPlusBanner: showLelandPlusBannerProp,
  lelandPlusContributors,
  hidePrice,
  skusData,
}) => {
  const isLelandPlusCategory =
    !!category && isLiveLelandPlusCategory(category.id);

  const showLelandPlusBanner = isLelandPlusCategory && showLelandPlusBannerProp;

  const { data: userData } = useCoachListUserQuery({
    skip: !showLelandPlusBanner,
  });

  const coachUrns = useMemo(
    () => staticCoaches.map((coach) => coach.id),
    [staticCoaches],
  );

  const { data: hydratedCoachesData } = useCoachListHydratedCoachesBySlugsQuery(
    {
      variables: { coachUrns },
      skip: !coachUrns.length,
    },
  );

  const hydratedCoachesBySlug = useMemo(
    () =>
      (hydratedCoachesData?.coachesBySlugs ?? []).reduce<
        Record<
          Scalars['CoachUrn']['output'],
          CoachListHydratedCoachFieldsFragment
        >
      >((coachMap, coach) => {
        coachMap[coach.id] = coach;
        return coachMap;
      }, {}),
    [hydratedCoachesData?.coachesBySlugs],
  );

  const isLelandPlusUser = !!userData?.user?.applicant?.hasLelandPlusAccess;
  const lelandPlusLandingUrl = getLelandPlusLandingPageUrl({
    isLelandPlusUser,
    categorySlug: category?.slug ?? '',
    subCategorySlug,
    utmMedium: 'srp',
  });

  if (loading) {
    return (
      <ul className={className}>
        <li>
          <CoachCardSkeleton />
        </li>
        <li className="border-t">
          <CoachCardSkeleton />
        </li>
        <li className="border-y">
          <CoachCardSkeleton />
        </li>
      </ul>
    );
  }

  if (error && !staticCoaches.length) {
    return <ul>{error.message}</ul>;
  }

  const coachesList = staticCoaches.map((staticCoachData, index) => {
    const coach = hydratedCoachesBySlug[staticCoachData.id]
      ? {
          ...staticCoachData,
          ...hydratedCoachesBySlug[staticCoachData.id],
        }
      : staticCoachData;

    const coachCard = (
      <CoachCard
        hidePrice={hidePrice}
        coach={coach}
        categoryId={category?.id}
        selectedSchoolIds={selectedSchoolIds}
        selectedCompanyIds={selectedCompanyIds}
        skusData={skusData}
        onClick={onClick ? () => onClick(coach, index) : undefined}
      />
    );

    return showLelandPlusBanner && index === 5 ? (
      <Fragment key={coach.id}>
        <li className="w-full py-6">
          <LelandPlusBanner
            ctaLabel={
              isLelandPlusUser ? 'View my dashboard' : 'Get started for free'
            }
            featuredCoaches={
              lelandPlusContributors?.results.map((coach) => ({
                ...coach.user,
                pictureLink: coach.user.pictureLink ?? undefined,
                id: coach.id,
                slug: coach.slug,
                url: getCoachUrl({
                  coachSlug: coach.slug,
                  categorySlug: category.slug,
                }),
              })) ?? []
            }
            numCoaches={lelandPlusContributors?.total ?? 0}
            landingPageUrl={lelandPlusLandingUrl}
            lelandPlusIcon={<LelandPlusLogo alt="Leland Plus Logo" />}
            onClick={() =>
              mxpnl.track('Leland+ Banner Click', {
                categorySlug: category?.slug,
                subCategorySlug,
                source: 'srp',
              })
            }
            imageCtas={getLelandPlusImages(category.id)}
            hideFreeTrial={isLelandPlusUser}
          />
        </li>
        <li className="border-y">{coachCard}</li>
      </Fragment>
    ) : (
      <li key={coach.id} className="border-b">
        {coachCard}
      </li>
    );
  });

  return (
    <ul className={`w-full overflow-hidden ${className}`}>
      {coachesList}
      {staticCoaches.length === 0 ? (
        <NoCoachResults
          heading="No results matching your filters"
          subheading={
            <>
              Try{' '}
              <button
                className="text-leland-gray-extra-light underline decoration-leland-gray-extra-light"
                onClick={resetVariables}
              >
                clearing your filters
              </button>{' '}
              or get free coach recommendations from a Leland team member.
            </>
          }
        />
      ) : null}
    </ul>
  );
};

export default CoachList;
